import React, { useState, useEffect } from 'react';
import {
    Box, Tabs, Tab, Typography, TextField, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Select, MenuItem, FormControl, InputLabel, Snackbar, Alert, Divider
} from './TailwindMuiStubs';
import { Plus as AddIcon, Trash2 as DeleteIcon, Save as SaveIcon } from 'lucide-react';
import API_URL from '../../config';

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ---- Tab 1: Domov ----
function TabDomov() {
    const [config, setConfig] = useState({});
    const [harmonogram, setHarmonogram] = useState([]);
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/web-config`).then(r => r.json()).then(setConfig).catch(() => {});
        fetch(`${API_URL}/harmonogram`).then(r => r.json()).then(setHarmonogram).catch(() => {});
    }, []);

    const saveConfig = async () => {
        await fetch(`${API_URL}/web-config`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(config) });
        setSnack({ severity: 'success', msg: 'Nastavenia uložené!' });
    };

    const saveHarmonogram = async (item) => {
        if (item.id) {
            await fetch(`${API_URL}/harmonogram/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
        } else {
            const r = await fetch(`${API_URL}/harmonogram`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
            const newItem = await r.json();
            setHarmonogram(prev => prev.map(h => h === item ? newItem : h));
        }
        setSnack({ severity: 'success', msg: 'Harmonogram uložený!' });
    };

    const deleteHarmonogram = async (item) => {
        if (item.id) await fetch(`${API_URL}/harmonogram/${item.id}`, { method: 'DELETE', credentials: 'include' });
        setHarmonogram(prev => prev.filter(h => h !== item));
    };

    const addHarmonogram = () => setHarmonogram(prev => [...prev, { cas: '', text: '', lokalita: '', order: prev.length }]);

    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold">Základné nastavenia</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <TextField label="Ročník (napr. 10.)" value={config.rocnik || ''} onChange={e => setConfig(p => ({ ...p, rocnik: e.target.value }))} fullWidth />
                <TextField label="Dátum (napr. 2. mája 2026)" value={config.datum || ''} onChange={e => setConfig(p => ({ ...p, datum: e.target.value }))} fullWidth />
                <TextField label="Hlavná trasa (napr. 31 km)" value={config.km_trasa || ''} onChange={e => setConfig(p => ({ ...p, km_trasa: e.target.value }))} fullWidth />
                <TextField label="Počet kategórií" value={config.pocet_kategorii || ''} onChange={e => setConfig(p => ({ ...p, pocet_kategorii: e.target.value }))} fullWidth />
            </Box>
            <TextField label="Intro text 1" value={config.intro_text_1 || ''} onChange={e => setConfig(p => ({ ...p, intro_text_1: e.target.value }))} fullWidth multiline rows={3} sx={{ mb: 2 }} />
            <TextField label="Intro text 2" value={config.intro_text_2 || ''} onChange={e => setConfig(p => ({ ...p, intro_text_2: e.target.value }))} fullWidth multiline rows={3} sx={{ mb: 3 }} />
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveConfig} sx={{ mb: 4 }}>Uložiť nastavenia</Button>

            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom fontWeight="bold">Harmonogram</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Čas</TableCell>
                            <TableCell>Text</TableCell>
                            <TableCell>Lokalita</TableCell>
                            <TableCell>Poradie</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {harmonogram.map((item, i) => (
                            <TableRow key={i}>
                                <TableCell><TextField size="small" value={item.cas} onChange={e => setHarmonogram(prev => prev.map((h, idx) => idx === i ? { ...h, cas: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.text} onChange={e => setHarmonogram(prev => prev.map((h, idx) => idx === i ? { ...h, text: e.target.value } : h))} fullWidth /></TableCell>
                                <TableCell><TextField size="small" value={item.lokalita || ''} onChange={e => setHarmonogram(prev => prev.map((h, idx) => idx === i ? { ...h, lokalita: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" type="number" value={item.order} onChange={e => setHarmonogram(prev => prev.map((h, idx) => idx === i ? { ...h, order: parseInt(e.target.value) } : h))} sx={{ width: 70 }} /></TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => saveHarmonogram(item)}><SaveIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => deleteHarmonogram(item)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} onClick={addHarmonogram}>Pridať položku</Button>

            {snack && <Snackbar open autoHideDuration={3000} onClose={() => setSnack(null)}><Alert severity={snack.severity}>{snack.msg}</Alert></Snackbar>}
        </Box>
    );
}

// ---- Tab 2: Pravidlá ----
function TabPravidla() {
    const [rules, setRules] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/rules`).then(r => r.json()).then(setRules).catch(() => {});
        fetch(`${API_URL}/routes`).then(r => r.json()).then(setRoutes).catch(() => {});
        fetch(`${API_URL}/rule-categories`).then(r => r.json()).then(setCategories).catch(() => {});
    }, []);

    const saveItem = async (collection, setCollection, item, endpoint) => {
        if (item.id) {
            await fetch(`${API_URL}/${endpoint}/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
        } else {
            const r = await fetch(`${API_URL}/${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
            const newItem = await r.json();
            setCollection(prev => prev.map(h => h === item ? newItem : h));
        }
        setSnack({ severity: 'success', msg: 'Uložené!' });
    };

    const deleteItem = async (collection, setCollection, item, endpoint) => {
        if (item.id) await fetch(`${API_URL}/${endpoint}/${item.id}`, { method: 'DELETE', credentials: 'include' });
        setCollection(prev => prev.filter(h => h !== item));
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold">Pravidlá</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nadpis</TableCell>
                            <TableCell>Obsah</TableCell>
                            <TableCell>Typ</TableCell>
                            <TableCell>Por.</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rules.map((item, i) => (
                            <TableRow key={i}>
                                <TableCell><TextField size="small" value={item.title} onChange={e => setRules(prev => prev.map((h, idx) => idx === i ? { ...h, title: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.content} onChange={e => setRules(prev => prev.map((h, idx) => idx === i ? { ...h, content: e.target.value } : h))} fullWidth multiline /></TableCell>
                                <TableCell>
                                    <Select size="small" value={item.type || 'standard'} onChange={e => setRules(prev => prev.map((h, idx) => idx === i ? { ...h, type: e.target.value } : h))}>
                                        <MenuItem value="standard">Štandard</MenuItem>
                                        <MenuItem value="info">Info</MenuItem>
                                        <MenuItem value="warning">Upozornenie</MenuItem>
                                        <MenuItem value="danger">Nebezpečné</MenuItem>
                                    </Select>
                                </TableCell>
                                <TableCell><TextField size="small" type="number" value={item.order} onChange={e => setRules(prev => prev.map((h, idx) => idx === i ? { ...h, order: parseInt(e.target.value) } : h))} sx={{ width: 60 }} /></TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => saveItem(rules, setRules, item, 'rules')}><SaveIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => deleteItem(rules, setRules, item, 'rules')}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} onClick={() => setRules(prev => [...prev, { title: '', content: '', type: 'standard', order: prev.length }])} sx={{ mb: 4 }}>Pridať pravidlo</Button>

            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom fontWeight="bold">Trasy</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Názov</TableCell>
                            <TableCell>km</TableCell>
                            <TableCell>Štart</TableCell>
                            <TableCell>Cieľ</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {routes.map((item, i) => (
                            <TableRow key={i}>
                                <TableCell><TextField size="small" value={item.name} onChange={e => setRoutes(prev => prev.map((h, idx) => idx === i ? { ...h, name: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" type="number" value={item.distance_km} onChange={e => setRoutes(prev => prev.map((h, idx) => idx === i ? { ...h, distance_km: e.target.value } : h))} sx={{ width: 70 }} /></TableCell>
                                <TableCell><TextField size="small" value={item.start_location || ''} onChange={e => setRoutes(prev => prev.map((h, idx) => idx === i ? { ...h, start_location: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.end_location || ''} onChange={e => setRoutes(prev => prev.map((h, idx) => idx === i ? { ...h, end_location: e.target.value } : h))} /></TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => saveItem(routes, setRoutes, item, 'routes')}><SaveIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => deleteItem(routes, setRoutes, item, 'routes')}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} onClick={() => setRoutes(prev => [...prev, { name: '', distance_km: 0, start_location: '', end_location: '', categories: [], order: prev.length }])} sx={{ mb: 4 }}>Pridať trasu</Button>

            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom fontWeight="bold">Kategórie a podmienky</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Názov (Nadpis)</TableCell>
                            <TableCell>Popis pravidiel</TableCell>
                            <TableCell>Poznámka</TableCell>
                            <TableCell>Štítok (Badge)</TableCell>
                            <TableCell>Farba</TableCell>
                            <TableCell>Poradie</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.sort((a,b)=>a.order-b.order).map((item, i) => (
                            <TableRow key={i}>
                                <TableCell><TextField size="small" value={item.title || ''} onChange={e => setCategories(prev => prev.map((h, idx) => idx === i ? { ...h, title: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" multiline value={item.text || ''} onChange={e => setCategories(prev => prev.map((h, idx) => idx === i ? { ...h, text: e.target.value } : h))} fullWidth /></TableCell>
                                <TableCell><TextField size="small" value={item.note || ''} onChange={e => setCategories(prev => prev.map((h, idx) => idx === i ? { ...h, note: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.badge || ''} onChange={e => setCategories(prev => prev.map((h, idx) => idx === i ? { ...h, badge: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.color || ''} onChange={e => setCategories(prev => prev.map((h, idx) => idx === i ? { ...h, color: e.target.value } : h))} sx={{ width: 120 }} /></TableCell>
                                <TableCell><TextField size="small" type="number" value={item.order} onChange={e => setCategories(prev => prev.map((h, idx) => idx === i ? { ...h, order: parseInt(e.target.value) } : h))} sx={{ width: 60 }} /></TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => saveItem(categories, setCategories, item, 'rule-categories')}><SaveIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => deleteItem(categories, setCategories, item, 'rule-categories')}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} onClick={() => setCategories(prev => [...prev, { title: '', text: '', note: '', badge: '', color: 'text-yellow-400', order: prev.length }])}>Pridať kategóriu</Button>

            {snack && <Snackbar open autoHideDuration={3000} onClose={() => setSnack(null)}><Alert severity={snack.severity}>{snack.msg}</Alert></Snackbar>}
        </Box>
    );
}

// ---- Tab 3: Výsledky (historické) ----
function TabVysledky() {
    const [yearLinks, setYearLinks] = useState([]);
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/year-overall-links`).then(r => r.json()).then(setYearLinks).catch(() => {});
    }, []);

    const save = async (item) => {
        if (item.id) {
            await fetch(`${API_URL}/year-overall-links/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
        } else {
            const r = await fetch(`${API_URL}/year-overall-links`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
            const newItem = await r.json();
            setYearLinks(prev => prev.map(h => h === item ? newItem : h));
        }
        setSnack({ severity: 'success', msg: 'Uložené!' });
    };

    const del = async (item) => {
        if (item.id) await fetch(`${API_URL}/year-overall-links/${item.id}`, { method: 'DELETE', credentials: 'include' });
        setYearLinks(prev => prev.filter(h => h !== item));
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold">Odkaz na celkové poradie (obrázok) podľa roku</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Nahrá URL obrázku (JPG/PNG) pre každý ročník. Zobrazí sa vo výsledkoch po kliknutí na "Celkové poradie".</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Rok</TableCell>
                            <TableCell>URL obrázku</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {yearLinks.map((item, i) => (
                            <TableRow key={i}>
                                <TableCell><TextField size="small" type="number" value={item.year} onChange={e => setYearLinks(prev => prev.map((h, idx) => idx === i ? { ...h, year: parseInt(e.target.value) } : h))} sx={{ width: 90 }} /></TableCell>
                                <TableCell><TextField size="small" value={item.image_url || ''} onChange={e => setYearLinks(prev => prev.map((h, idx) => idx === i ? { ...h, image_url: e.target.value } : h))} fullWidth /></TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => save(item)}><SaveIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => del(item)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} onClick={() => setYearLinks(prev => [...prev, { year: new Date().getFullYear(), image_url: '' }])}>Pridať ročník</Button>
            {snack && <Snackbar open autoHideDuration={3000} onClose={() => setSnack(null)}><Alert severity={snack.severity}>{snack.msg}</Alert></Snackbar>}
        </Box>
    );
}

// ---- Tab 4: Galéria ----
function TabGaleria() {
    const [galleries, setGalleries] = useState([]);
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/gallery-years`).then(r => r.json()).then(setGalleries).catch(() => {});
    }, []);

    const save = async (item) => {
        if (item.id) {
            await fetch(`${API_URL}/gallery-years/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
        } else {
            const r = await fetch(`${API_URL}/gallery-years`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
            const newItem = await r.json();
            setGalleries(prev => prev.map(h => h === item ? newItem : h));
        }
        setSnack({ severity: 'success', msg: 'Uložené!' });
    };

    const del = async (item) => {
        if (item.id) await fetch(`${API_URL}/gallery-years/${item.id}`, { method: 'DELETE', credentials: 'include' });
        setGalleries(prev => prev.filter(h => h !== item));
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold">Galérie podľa ročníkov</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Rok</TableCell>
                            <TableCell>Názov</TableCell>
                            <TableCell>Titulná fotka (URL)</TableCell>
                            <TableCell>Google Photos URL</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {galleries.map((item, i) => (
                            <TableRow key={i}>
                                <TableCell><TextField size="small" type="number" value={item.year} onChange={e => setGalleries(prev => prev.map((h, idx) => idx === i ? { ...h, year: parseInt(e.target.value) } : h))} sx={{ width: 90 }} /></TableCell>
                                <TableCell><TextField size="small" value={item.title} onChange={e => setGalleries(prev => prev.map((h, idx) => idx === i ? { ...h, title: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.cover_image_url || ''} onChange={e => setGalleries(prev => prev.map((h, idx) => idx === i ? { ...h, cover_image_url: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.google_photos_url || ''} onChange={e => setGalleries(prev => prev.map((h, idx) => idx === i ? { ...h, google_photos_url: e.target.value } : h))} /></TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => save(item)}><SaveIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => del(item)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} onClick={() => setGalleries(prev => [...prev, { year: new Date().getFullYear(), title: '', cover_image_url: '', google_photos_url: '' }])}>Pridať galériu</Button>
            {snack && <Snackbar open autoHideDuration={3000} onClose={() => setSnack(null)}><Alert severity={snack.severity}>{snack.msg}</Alert></Snackbar>}
        </Box>
    );
}

// ---- Tab 5: Rekordy ----
function TabRekordy() {
    const [records, setRecords] = useState([]);
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/records`).then(r => r.json()).then(setRecords).catch(() => {});
    }, []);

    const save = async (item) => {
        if (item.id) {
            await fetch(`${API_URL}/records/${item.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
        } else {
            const r = await fetch(`${API_URL}/records`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(item) });
            const newItem = await r.json();
            setRecords(prev => prev.map(h => h === item ? newItem : h));
        }
        setSnack({ severity: 'success', msg: 'Rekord uložený!' });
    };

    const del = async (item) => {
        if (item.id) await fetch(`${API_URL}/records/${item.id}`, { method: 'DELETE', credentials: 'include' });
        setRecords(prev => prev.filter(h => h !== item));
    };

    const add = () => setRecords(prev => [...prev, { category: '', time: '', names: '', year: new Date().getFullYear(), order: prev.length }]);

    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold">Správa traťových rekordov</Typography>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Kategória</TableCell>
                            <TableCell>Čas</TableCell>
                            <TableCell>Meno / Posádka</TableCell>
                            <TableCell>Rok</TableCell>
                            <TableCell>Poradie</TableCell>
                            <TableCell>Akcie</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.sort((a,b)=>a.order-b.order).map((item, i) => (
                            <TableRow key={i}>
                                <TableCell><TextField size="small" value={item.category || ''} onChange={e => setRecords(prev => prev.map((h, idx) => idx === i ? { ...h, category: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.time || ''} onChange={e => setRecords(prev => prev.map((h, idx) => idx === i ? { ...h, time: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" value={item.names || ''} onChange={e => setRecords(prev => prev.map((h, idx) => idx === i ? { ...h, names: e.target.value } : h))} /></TableCell>
                                <TableCell><TextField size="small" type="number" value={item.year || ''} onChange={e => setRecords(prev => prev.map((h, idx) => idx === i ? { ...h, year: parseInt(e.target.value) } : h))} sx={{ width: 90 }} /></TableCell>
                                <TableCell><TextField size="small" type="number" value={item.order} onChange={e => setRecords(prev => prev.map((h, idx) => idx === i ? { ...h, order: parseInt(e.target.value) } : h))} sx={{ width: 70 }} /></TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => save(item)}><SaveIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => del(item)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button startIcon={<AddIcon />} onClick={add}>Pridať rekord</Button>
            {snack && <Snackbar open autoHideDuration={3000} onClose={() => setSnack(null)}><Alert severity={snack.severity}>{snack.msg}</Alert></Snackbar>}
        </Box>
    );
}

// ---- Tab 6: Ceny ----
function TabCeny() {
    const [prices, setPrices] = useState({ start_fee_per_person: 0, canoe_rental_fee: 0, catamaran_rental_fee: 0, fixed_price_categories: {} });
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/prices`).then(r => r.json()).then(setPrices).catch(() => {});
    }, []);

    const savePrices = async () => {
        try {
            await fetch(`${API_URL}/prices`, { 
                method: 'PUT', 
                headers: { 'Content-Type': 'application/json' }, 
                credentials: 'include', 
                body: JSON.stringify(prices) 
            });
            setSnack({ severity: 'success', msg: 'Ceny úspešne uložené!' });
        } catch (error) {
            setSnack({ severity: 'error', msg: 'Chyba pri ukladaní cien.' });
        }
    };

    const handleFixedCategoryChange = (categoryId, field, value) => {
        setPrices(prev => ({
            ...prev,
            fixed_price_categories: {
                ...prev.fixed_price_categories,
                [categoryId]: {
                    ...prev.fixed_price_categories[categoryId],
                    [field]: parseInt(value) || 0
                }
            }
        }));
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold">Základné poplatky</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 4, maxWidth: 900 }}>
                <TextField 
                    label="Štartovné na osobu (€)" 
                    type="number" 
                    value={prices.start_fee_per_person} 
                    onChange={e => setPrices(p => ({ ...p, start_fee_per_person: parseInt(e.target.value) || 0 }))} 
                    fullWidth 
                />
                <TextField 
                    label="Prenájom kanoe (€)" 
                    type="number" 
                    value={prices.canoe_rental_fee} 
                    onChange={e => setPrices(p => ({ ...p, canoe_rental_fee: parseInt(e.target.value) || 0 }))} 
                    fullWidth 
                />
                <TextField 
                    label="Prenájom katamaránu (€)" 
                    type="number" 
                    value={prices.catamaran_rental_fee} 
                    onChange={e => setPrices(p => ({ ...p, catamaran_rental_fee: parseInt(e.target.value) || 0 }))} 
                    fullWidth 
                />
            </Box>

            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom fontWeight="bold">Fixné ceny kategórií (napr. Štafeta)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Nastav počet posádky a lodí, ktoré sa im fixne naúčtujú, nezávisle od toho, čo si naklikajú vo formulári.</Typography>
            
            <TableContainer component={Paper} sx={{ mb: 4, maxWidth: 600 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID Kategórie</TableCell>
                            <TableCell>Fixná veľkosť posádky (ks)</TableCell>
                            <TableCell>Fixné množstvo lodí (ks)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(prices.fixed_price_categories).map(([categoryId, data]) => (
                            <TableRow key={categoryId}>
                                <TableCell><Typography fontWeight="bold">{categoryId}</Typography></TableCell>
                                <TableCell>
                                    <TextField 
                                        size="small" 
                                        type="number" 
                                        value={data.crew_size} 
                                        onChange={e => handleFixedCategoryChange(categoryId, 'crew_size', e.target.value)} 
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField 
                                        size="small" 
                                        type="number" 
                                        value={data.canoe_rentals} 
                                        onChange={e => handleFixedCategoryChange(categoryId, 'canoe_rentals', e.target.value)} 
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {Object.keys(prices.fixed_price_categories).length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">Žiadne fixné kategórie</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Button variant="contained" startIcon={<SaveIcon />} onClick={savePrices}>Uložiť ceny</Button>
            {snack && <Snackbar open autoHideDuration={3000} onClose={() => setSnack(null)}><Alert severity={snack.severity}>{snack.msg}</Alert></Snackbar>}
        </Box>
    );
}

// ---- Hlavný WebSettings komponent ----
export default function WebSettings() {
    const [tab, setTab] = useState(0);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Nastavenia webu</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Tu môžeš editovať obsah verejného webu — harmonogram, pravidlá, trasy, výsledky, galériu a ceny štartovného.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Domov" />
                    <Tab label="Pravidlá a Trasy" />
                    <Tab label="Výsledky" />
                    <Tab label="Galéria" />
                    <Tab label="Rekordy" />
                    <Tab label="Ceny" />
                </Tabs>
            </Box>

            <TabPanel value={tab} index={0}><TabDomov /></TabPanel>
            <TabPanel value={tab} index={1}><TabPravidla /></TabPanel>
            <TabPanel value={tab} index={2}><TabVysledky /></TabPanel>
            <TabPanel value={tab} index={3}><TabGaleria /></TabPanel>
            <TabPanel value={tab} index={4}><TabRekordy /></TabPanel>
            <TabPanel value={tab} index={5}><TabCeny /></TabPanel>
        </Box>
    );
}
